import React, { useState, useEffect, useRef } from "react";
import { fetchSTLFiles } from "../../services/githubService";
import { useTheme } from "../../Hooks/UseData";
import ModelCard from "./ModelCard";
import ModelModal from "./ModelModal";

const Portfolio = () => {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedModel, setSelectedModel] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const themeColor = useTheme();
    const deepLinkHandled = useRef(false);

    useEffect(() => {
        loadModels();
    }, []);

    // Deep linking: auto-open model from ?model= query param
    useEffect(() => {
        if (!loading && models.length > 0 && !deepLinkHandled.current) {
            const params = new URLSearchParams(window.location.search);
            const modelName = params.get("model");
            if (modelName) {
                const match = models.find(
                    (m) => m.name.toLowerCase() === modelName.toLowerCase()
                );
                if (match) {
                    deepLinkHandled.current = true;
                    // Small delay to let the tab switch animation finish
                    setTimeout(() => {
                        setSelectedModel(match);
                        setIsModalOpen(true);
                    }, 500);
                }
            }
        }
    }, [loading, models]);

    const loadModels = async () => {
        try {
            setLoading(true);
            setError(null);
            const files = await fetchSTLFiles();
            setModels(files);
        } catch (err) {
            setError("Failed to load CAD models. Please try again later.");
            console.error("Error loading models:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleModelClick = (model) => {
        setSelectedModel(model);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedModel(null), 300);
    };

    return (
        <section className="main-content">
            <div className="container">
                {/* Loading State */}
                {loading && (
                    <div className="portfolio-loading">
                        <div className="loading-spinner"></div>
                        <p className="open-sans-font">Loading CAD models from GitHub...</p>
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div className="portfolio-error">
                        <i className="fa fa-exclamation-triangle"></i>
                        <p className="open-sans-font">{error}</p>
                        <button className="button" onClick={loadModels}>
                            <span className="button-text">Retry</span>
                            <span className="button-icon fa fa-refresh"></span>
                        </button>
                    </div>
                )}

                {/* Success State - Model Grid */}
                {!loading && !error && models.length > 0 && (
                    <>
                        <div className="portfolio-intro" data-aos="fade-up">
                            <p className="open-sans-font">
                                Explore my CAD design work created using <strong>Siemens NX</strong>.
                                Click on any model to view it in interactive 3D. All models are dynamically
                                loaded from my{" "}
                                <a
                                    href="https://github.com/ajeshbs/cad"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: themeColor }}
                                >
                                    GitHub repository
                                </a>.
                            </p>
                            <div className="portfolio-stats">
                                <div className="stat-item">
                                    <span className="stat-number" style={{ color: themeColor }}>
                                        {models.length}
                                    </span>
                                    <span className="stat-label">CAD Models</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-number" style={{ color: themeColor }}>
                                        NX
                                    </span>
                                    <span className="stat-label">Siemens Software</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-number" style={{ color: themeColor }}>
                                        STL
                                    </span>
                                    <span className="stat-label">File Format</span>
                                </div>
                            </div>
                        </div>

                        <hr className="separator mt-1" />

                        <div className="row portfolio-grid">
                            {models.map((model, index) => (
                                <div
                                    key={model.sha || index}
                                    className="col-12 col-md-6 col-lg-4 portfolio-item"
                                >
                                    <ModelCard model={model} onClick={handleModelClick} />
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* Empty State */}
                {!loading && !error && models.length === 0 && (
                    <div className="portfolio-empty">
                        <i className="fa fa-cube"></i>
                        <p className="open-sans-font">No CAD models found.</p>
                    </div>
                )}
            </div>

            {/* 3D Model Modal */}
            <ModelModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                model={selectedModel}
                themeColor={themeColor}
            />
        </section>
    );
};

export default Portfolio;
